import React, { type FC } from 'react';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { Box } from '@atlaskit/primitives/compiled';

import { InspectPermissionsDialog } from '@confluence/inspect-permissions/entry-points/InspectPermissionsDialog';
import {
	AccessDetailsDialog as SpaceAccessDetailsDialog,
	type AccessDetailsDialogProps,
} from '@confluence/space-access-details/entry-points/AccessDetailsDialog';

import {
	ShareAndRestrictDialog,
	type ShareAndRestrictDialogProps,
} from './shareAndRestrictDialog/ShareAndRestrictDialog';
import {
	CloseConfirmationDialog,
	type CloseConfirmationDialogProps,
} from './CloseConfirmationDialog';
import {
	GroupNotifyWarningDialog,
	type GroupNotifyWarningDialogProps,
} from './GroupNotifyWarningDialog';
import type { InspectPermsDialogProps } from './shareAndRestrictDialog/header/OverflowMenu';
import {
	RestrictedAccessWarningDialog,
	type RestrictedAccessWarningDialogProps,
} from './resolveRestrictions/RestrictedAccessWarningDialog';
import {
	ResolveRestrictionsDialog,
	type ResolveRestrictionsDialogProps,
} from './resolveRestrictions/ResolveRestrictionsDialog';
import {
	OpenAccessWarningDialog,
	type OpenAccessWarningDialogProps,
} from './openAccessWarning/OpenAccessWarningDialog';

export type DialogsProps = {
	shareAndRestrictDialog?: ShareAndRestrictDialogProps;
	restrictedAccessWarningDialog?: RestrictedAccessWarningDialogProps;
	resolveRestrictionsDialog?: ResolveRestrictionsDialogProps;
	groupNotifyWarningDialog?: GroupNotifyWarningDialogProps;
	closeConfirmationDialog?: CloseConfirmationDialogProps;
	inspectPermsDialog?: InspectPermsDialogProps;
	spaceAccessDetails?: AccessDetailsDialogProps;
	openAccessWarningDialog?: OpenAccessWarningDialogProps;
	// Props for other dialog possibilities would go here
};

/**
 * A common mounting host for all the top-level dialogs the USD might have
 */
export const Dialogs: FC<DialogsProps> = ({
	shareAndRestrictDialog,
	restrictedAccessWarningDialog,
	resolveRestrictionsDialog,
	groupNotifyWarningDialog,
	closeConfirmationDialog,
	spaceAccessDetails,
	inspectPermsDialog,
	openAccessWarningDialog,
}: DialogsProps) => (
	// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
	<Box onClick={(event: React.MouseEvent) => event?.stopPropagation()}>
		<ModalTransition>
			{shareAndRestrictDialog && <ShareAndRestrictDialog {...shareAndRestrictDialog} />}
		</ModalTransition>
		<ModalTransition>
			{groupNotifyWarningDialog && <GroupNotifyWarningDialog {...groupNotifyWarningDialog} />}
		</ModalTransition>
		<ModalTransition>
			{closeConfirmationDialog && <CloseConfirmationDialog {...closeConfirmationDialog} />}
		</ModalTransition>
		<ModalTransition>
			{spaceAccessDetails && <SpaceAccessDetailsDialog {...spaceAccessDetails} />}
		</ModalTransition>
		{/* Using ModalTransition here is preventing InspectPermissionsDialog from re-rendering after first time */}
		{inspectPermsDialog && <InspectPermissionsDialog {...inspectPermsDialog} />}
		<ModalTransition>
			{restrictedAccessWarningDialog && (
				<RestrictedAccessWarningDialog {...restrictedAccessWarningDialog} />
			)}
		</ModalTransition>
		<ModalTransition>
			{resolveRestrictionsDialog && <ResolveRestrictionsDialog {...resolveRestrictionsDialog} />}
		</ModalTransition>
		<ModalTransition>
			{openAccessWarningDialog && <OpenAccessWarningDialog {...openAccessWarningDialog} />}
		</ModalTransition>
	</Box>
);
